// Rückfrage — EIN Baustein für jede Sicherheitsabfrage, die heute `window.confirm()` ist.
//
// Gemessener Stand vor dem Zusammenlegen: SIEBZEHN Aufrufe von `window.confirm()` in zwölf
// Dateien, und zwar vor den folgenreichsten Aktionen der Anwendung — Inhalte, Alben, Playlists,
// Interpreten, MindBooks, Benutzer, Themen und Dateien löschen bzw. abschalten. Ein geteilter
// Baustein dafür existierte nicht; es ist dieselbe Fundstelle desselben Kopier-Musters wie zuvor
// beim Balken und beim Blätter-Fuß, nur dass die Kopie hier der Browser selbst ist.
//
// **Warum der native Dialog weg muss** — jeder Punkt ist ein Verlust, kein Geschmack:
//
// | Was der native Dialog tut                              | Folge                                          |
// |--------------------------------------------------------|------------------------------------------------|
// | hält den Hauptfaden an (`window.confirm` ist synchron)  | die Seite friert ein, laufende Animationen und Zeitmesser stehen |
// | ist nicht gestaltbar                                    | „Löschen" sieht aus wie „Trotzdem speichern?" — die Gefahr ist nicht ablesbar |
// | trägt keine Rolle für Vorlesehilfen                     | Titel und Begründung sind nicht verknüpft, die Ansage ist Browser-Sache |
// | sieht in jedem Browser anders aus                       | die Knopf-Reihenfolge wechselt; wer „rechts ist bestätigen" gelernt hat, klickt in Firefox daneben |
// | kennt nur eine Zeichenkette                             | kein Titel, keine Gefahren-Kennzeichnung, keine eigene Beschriftung der Knöpfe |
//
// **Was dieser Baustein dafür wieder herstellen MUSS** — das ist der halbe Grund für ihn, nicht die
// Kür: der native Dialog konnte Fokusfang, Escape, Fokus-Rückkehr und eine angesagte Rolle von
// Haus aus. Ein selbstgebauter Dialog, der das nicht nachbaut, ist gegenüber `window.confirm()` ein
// RÜCKSCHRITT für alle, die mit Tastatur oder Vorlesehilfe arbeiten. Umgesetzt und je einzeln
// getestet in `__tests__/Rueckfrage.test.tsx`:
//
// - `role="alertdialog"` bzw. `role="dialog"` mit `aria-modal`, verknüpft über `aria-labelledby`
//   (Titel) und `aria-describedby` (Text);
// - Fokusfang: Tab und Shift+Tab laufen im Kreis, sie wandern nicht hinter den Dialog;
// - Escape bricht ab — und zwar nur den Dialog, nicht das Formular darunter;
// - der Fokus kehrt beim Schließen auf das auslösende Element zurück;
// - voreingestellt ist ABBRECHEN, nicht der Gefahren-Knopf.
//
// **Was dieser Baustein bewusst NICHT tut:** ein Klick auf den Schleier schließt nichts. Der native
// Dialog kannte keinen solchen Ausweg, und bei einer Löschabfrage ist ein versehentlich danebener
// Klick der wahrscheinlichste Klick überhaupt. Es bleibt bei zwei ausdrücklichen Wegen hinaus:
// „Abbrechen" und Escape.
//
// Zustand und Versprechen liegen NICHT hier, sondern in `useRueckfrage` — sonst schriebe die
// nächste Seite ihre eigene `useState`-Weiche und ihr eigenes `Promise` ab, und genau daraus
// entstünde die nächste Kopie.
//
// Aus demselben Grund liegen Fokusfang, Escape und Fokus-Rückkehr seit dem Ausbau NICHT mehr hier,
// sondern in `hooks/useFokusfang.ts`. Anlass war die Messung an den drei Dialogen der Mediathek:
// die fangen den Fokus nicht, sind aber FORMULAR-Dialoge (Eingabefeld statt Ja/Nein) und lassen
// sich deshalb nicht durch diesen Baustein ersetzen. Geteilt wird also das, was ihnen fehlt.
import React, { useId, useRef } from 'react';
import { useFokusfang } from '../../hooks/useFokusfang';
import { ModalSchicht } from './ModalSchicht';

/** Was gefragt wird. Alles außer Titel und Text hat eine tragfähige Vorgabe. */
export interface RückfrageWunsch {
  /** Überschrift des Dialogs, kurz. Wird als Name des Dialogs angesagt. */
  titel: string;
  /**
   * Die Begründung darunter. Zeilenumbrüche bleiben erhalten (`whitespace-pre-line`) — zwei der
   * gemessenen Aufrufe setzen `\n\n` als Absatztrenner, und `window.confirm()` hat den geehrt.
   */
  text: string;
  /** Beschriftung des bestätigenden Knopfes. Ohne Angabe „Löschen" bei `gefahr`, sonst „Weiter". */
  bestätigenText?: string;
  /** Beschriftung des abbrechenden Knopfes. */
  abbrechenText?: string;
  /**
   * Kennzeichnet die Frage als folgenreich: der bestätigende Knopf wird rot, und der Dialog meldet
   * sich als `alertdialog` statt als `dialog`.
   *
   * Die Unterscheidung ist keine Kosmetik. Von den siebzehn gemessenen Aufrufen sind neun echte
   * Löschungen bzw. Abschaltungen; die übrigen acht fragen „Trotzdem veröffentlichen?" oder
   * „Trotzdem so speichern?" und sind folgenlos rücknehmbar. Sähen beide gleich aus, verlöre die
   * Farbe genau dort ihre Aussage, wo sie gebraucht wird.
   */
  gefahr?: boolean;
}

interface RueckfrageProps {
  /** Die offene Frage, oder `null`. Kommt aus `useRueckfrage`. */
  rückfrage: RückfrageWunsch | null;
  /** Die Antwort der Nutzerin. `true` = bestätigt, `false` = abgebrochen. */
  onAntwort: (bestätigt: boolean) => void;
}

/**
 * Stapelhöhe des Rückfrage-Schleiers — der einzige Wert dieser Anwendung, der nicht `z-50` ist und
 * nicht `z-[60]`.
 *
 * ⚠️ Am gebauten Stylesheet nachgemessen, nicht angenommen. Es gibt genau zwei Nachbarn:
 *
 * - **`z-50` — jedes Overlay der Anwendung.** Die beiden Themen-Formulare (`ModalSchicht`),
 *   `.modal-overlay` und `.bunny-file-picker-overlay` in `index.css`, der Titel-Wähler in
 *   `playlists/TrackPicker.tsx`. Die Rückfrage MUSS darüber liegen: mehrere der siebzehn
 *   Fundstellen stellen ihre Frage aus einem offenen Formular heraus (`AlbumForm`, `ArtistForm`,
 *   `PlaylistForm`, `AudioForm`). Bei Gleichstand entschiede die Dokumentreihenfolge, und beide
 *   Schichten hängen am selben `document.body` — die Rückfrage gewönne heute zufällig, weil sie
 *   später montiert wird. Eine Sicherheitsabfrage, deren Sichtbarkeit an einer Montage-Reihenfolge
 *   hängt, ist keine.
 *
 * - **`z-[60]` — der schwebende Benachrichtigungs-Balken.** Die Rückfrage bleibt bewusst DARUNTER.
 *   Sie ist zwar das oberste Bedienelement, aber ihr Schleier deckt die ganze Seite: läge sie über
 *   dem Balken, verschwände eine stehende Fehlermeldung hinter 50 % Schwarz, sobald irgendwo eine
 *   Rückfrage aufgeht. Das ist genau der Fehler, den die Schiene davor abgeräumt hat („die Meldung
 *   sieht niemand"), einmal mehr. Der Balken liegt oben rechts, das Dialogfeld mittig — sie
 *   überlappen ohnehin nicht, es geht allein um den Schleier dazwischen.
 *
 * `z-[55]` ist damit nicht „irgendwas dazwischen", sondern der einzige Wert, der beide Bedingungen
 * erfüllt. Der Balken behält seinen einzigen Knopf („Schließen", nur am Fehler) anklickbar; er
 * liegt außerhalb des Fokusfangs und ist per Tastatur nicht erreichbar, solange der Dialog offen
 * ist — richtig so, denn er gehört nicht zur gestellten Frage.
 */
const RÜCKFRAGE_STAPEL = 'z-[55]';

/**
 * Gefahren-Kennzeichnung des bestätigenden Knopfes.
 *
 * ⚠️ Gemessen, nicht erfunden: eine Klasse `btn-danger` gibt es in `index.css` NICHT — dort stehen
 * nur `btn-primary`, `btn-secondary`, `btn-outline` und `btn-ghost`. Die Anwendung kennzeichnet
 * Gefahr bislang mit den `error-*`-Tokens aus `tailwind.config.js`, und zwar in zwei Ausprägungen:
 * als Text-Verweis (`text-error-600 hover:text-error-800`, so die Löschen-Knöpfe in `AlbumForm`,
 * `ArtistForm`, `PlaylistForm`) und als gefüllte Fläche (`bg-error-500 hover:bg-error-600
 * text-white`, so die Entfernen-Marken in `AudioForm` und `VideoForm`).
 *
 * Hier gewinnt die gefüllte Fassung: der Knopf ist die letzte Handlung vor einer Löschung und
 * steht neben einem gleich großen „Abbrechen". Ein Text-Verweis wäre an dieser Stelle das
 * schwächere von zwei Elementen — die Gefahr sähe harmloser aus als der Rückzug. `error-600` statt
 * `error-500`, weil der Knopf weißen Text trägt (`#dc2626` gegen Weiß erreicht den Kontrastwert
 * 4.5:1, `#ef4444` nicht).
 *
 * Der Fokusring der Basis-`btn` zeigt `primary` — hier auf `error` gedreht, sonst umrandete sich
 * der rote Knopf blau. Dieselbe Kombination fährt `AudioForm.tsx` bereits an seinem
 * Löschen-Knopf.
 */
const KNOPF_GEFAHR = 'bg-error-600 text-white hover:bg-error-700 active:bg-error-800 focus-visible:ring-error-500';

/**
 * Der eigentliche Dialog. Bewusst eine eigene Komponente hinter der `null`-Weiche, damit „offen"
 * gleich „montiert" ist: Fokus merken, Fokus setzen und Fokus zurückgeben hängen damit an
 * Montage und Abbau statt an einer selbstgebauten Zustandsbeobachtung.
 */
const RückfrageDialog: React.FC<{ rückfrage: RückfrageWunsch; onAntwort: (b: boolean) => void }> = ({
  rückfrage,
  onAntwort,
}) => {
  const titelId = useId();
  const textId = useId();
  const abbrechenRef = useRef<HTMLButtonElement>(null);

  // Fokusfang, Escape und Fokus-Rückkehr kommen aus dem geteilten Haken. Zwei Angaben sind hier
  // nicht Vorgabe, sondern Zusage:
  //
  // - `anfangsfokus` = ABBRECHEN, nicht der Gefahren-Knopf. Wer den Dialog mit der Tastatur
  //   bedient und reflexhaft Enter drückt, darf damit nichts löschen. Ausdrücklich benannt und
  //   nicht dem „ersten Ziel im Dialog" überlassen: sonst hinge die Zusage an der Reihenfolge im
  //   Baum und ginge beim nächsten Umbau der Knopfleiste still verloren.
  // - `anfangsschlüssel` = die Frage selbst. Stellt der Aufrufer zwei Fragen kurz hintereinander,
  //   bleibt der Dialog montiert — ohne den Schlüssel stünde der Fokus bei der zweiten Frage noch
  //   dort, wo ihn die erste hinterlassen hat, unter Umständen also auf „Löschen".
  const flächeRef = useFokusfang<HTMLDivElement>({
    beiEscape: () => onAntwort(false),
    anfangsfokus: abbrechenRef,
    anfangsschlüssel: rückfrage,
  });

  const istGefahr = rückfrage.gefahr === true;

  return (
    <ModalSchicht stapelKlasse={RÜCKFRAGE_STAPEL}>
      <div
        ref={flächeRef}
        // `alertdialog` nur bei Gefahr: die Rolle sagt Vorlesehilfen „hier steht eine dringende
        // Meldung, die eine Antwort verlangt", und viele lesen den beschreibenden Text dann sofort
        // vor. Für „Trotzdem veröffentlichen?" wäre das eine Dringlichkeit, die es nicht gibt —
        // und eine Rolle, die überall dringend ruft, wird überall überhört.
        role={istGefahr ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titelId}
        aria-describedby={textId}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <h2 id={titelId} className="text-xl font-bold text-secondary-900">
          {rückfrage.titel}
        </h2>
        <p id={textId} className="mt-3 text-sm text-secondary-700 whitespace-pre-line">
          {rückfrage.text}
        </p>
        <div className="mt-6 flex items-center justify-end gap-3">
          {/* Abbrechen steht LINKS und ist der voreingestellte Fokus. Beides zusammen ist die
              Absicherung: der Weg des geringsten Widerstands führt aus der Aktion heraus. */}
          <button
            type="button"
            ref={abbrechenRef}
            onClick={() => onAntwort(false)}
            className="btn btn-outline cursor-pointer"
          >
            {rückfrage.abbrechenText ?? 'Abbrechen'}
          </button>
          <button
            type="button"
            onClick={() => onAntwort(true)}
            className={`btn cursor-pointer ${istGefahr ? KNOPF_GEFAHR : 'btn-primary'}`}
          >
            {rückfrage.bestätigenText ?? (istGefahr ? 'Löschen' : 'Weiter')}
          </button>
        </div>
      </div>
    </ModalSchicht>
  );
};

export const Rueckfrage: React.FC<RueckfrageProps> = ({ rückfrage, onAntwort }) =>
  rückfrage ? <RückfrageDialog rückfrage={rückfrage} onAntwort={onAntwort} /> : null;
